import cloneDeep from 'lodash.cloneDeep';
import { CompensationTrigger } from '../../shared/enums/compensation-trigger.enum';
import { textEncodeBase64 } from '../../utils/encode';
import { TemplateDocumentTypeEnum } from '../enums/template-document-type.enum';
import { IncentiveProgramSigning } from '../models/incentive-program-signing.model';
import { TemplateDocumentDetails, TemplateDocumentDocumentType } from '../models/linked-template.model';
import { TemplateDetails } from '../models/template-details.model';
import { TemplateSummary, TemplateSummaryLinkUnlink } from '../models/template-summary.model';
import { PaymentCadence } from '../../program-enrollments-signing/enums/payment-cadence-signing.enum';

export function isIncentiveProgramActive(incentiveProgram: IncentiveProgramSigning): boolean {
  const conditionActiveUntil = incentiveProgram.activeUntil === null ||
    new Date(incentiveProgram.activeUntil).getTime() > new Date().getTime();
  return incentiveProgram.isActive && conditionActiveUntil;
}

export function getIncentiveProgramStatus(incentiveProgram: IncentiveProgramSigning): string {
  if (isIncentiveProgramExpired(incentiveProgram)) {
    return "Expired";
  }

  if (isIncentiveProgramDeactivated(incentiveProgram)) {
    return "Deactivated";
  }

  if (!incentiveProgram.isActive) {
    return "Draft";

  }

  return "Activated";
}

function mapTemplateSummaryToTemplateSummaryLinkUnlink(templateSummary: TemplateSummary): TemplateSummaryLinkUnlink {
  return {
    externalTemplateId: templateSummary.referenceId,
    linkStatus: templateSummary.status
  }
}

export function mapTemplateSummariesToTemplateSummaryLinkUnlink(templateSummaries: TemplateSummary[]): TemplateSummaryLinkUnlink[] {
  return templateSummaries.map(template => mapTemplateSummaryToTemplateSummaryLinkUnlink(template));
}

export function mapTemplateDocumentDetailsToTemplateDocumentDocumentTypes(documents: TemplateDocumentDetails[])
  : TemplateDocumentDocumentType[] {
  return documents.map(document => ({
    id: document.id,
    type: document.type.type
  } as TemplateDocumentDocumentType));
}

export function getPaymentCadenceValues(): string[] {
  return Object.keys(PaymentCadence).filter(value => isNaN(Number(value)))
}

export function getCompensationTriggerValues(): string[] {
  return Object.keys(CompensationTrigger).filter(value => isNaN(Number(value)))
}

export function isIncentiveProgramExpired(incentiveProgram: IncentiveProgramSigning): boolean {
  return !incentiveProgram?.deactivatedOn
    && !!incentiveProgram?.activeUntil && new Date() > new Date(incentiveProgram?.activeUntil);
}

export function isIncentiveProgramDeactivated(incentiveProgram: IncentiveProgramSigning): boolean {
  return !!incentiveProgram?.deactivatedOn && !incentiveProgram.isActive;
}

export function hasTemplateEnrollmentAgreementDocument(templateDetails: TemplateDetails): boolean {
  return templateDetails?.documents?.some(doc => doc.type?.type === TemplateDocumentTypeEnum.EnrollmentAgreement) ?? false;
}

export function encodeTextFields(incentiveProgram: IncentiveProgramSigning): IncentiveProgramSigning {
  const incentiveProgramCopy: IncentiveProgramSigning = cloneDeep(incentiveProgram);

  incentiveProgramCopy.name = textEncodeBase64(incentiveProgramCopy.name);
  incentiveProgramCopy.accountNumber = textEncodeBase64(incentiveProgramCopy.accountNumber);
  if (!!incentiveProgramCopy.description) {
    incentiveProgramCopy.description = textEncodeBase64(incentiveProgramCopy.description);
  }

  return incentiveProgramCopy;
}
